/**
 * Mensajes AUTOMÁTICOS del sistema (ej. el manager-alert del follow-up) se guardan como
 * TEXTO en el DM — acá se traducen AL RENDERIZAR según el idioma del que los ve (EN para
 * quien usa la app en inglés, ES en español). Cubre también los ya guardados: se parsea
 * el patrón en cualquiera de los dos idiomas y se re-arma en el idioma del viewer.
 * El status/depto se muestran tal cual (en la app no se traducen los nombres de colas).
 */

// [Cliente](/claims/id) o texto plano hasta el separador.
const CLIENT = String.raw`(\[[^\]]+\]\([^)]+\)|[^·]+?)`;
const ES_RE = new RegExp(`^⚠️ No he hecho follow up en ${CLIENT} · está en \\*\\*(.+?)\\*\\* \\((.+?)\\) · necesito ayuda$`);
const EN_RE = new RegExp(`^⚠️ I haven't followed up on ${CLIENT} · it's in \\*\\*(.+?)\\*\\* \\((.+?)\\) · I need help$`);

// La campana del share vencido (screenShare/sweep.ts) se guarda en inglés fijo: se re-arma acá.
const SHARE_EN_RE = /^(.+?) tried to share their screen with you$/;
const SHARE_ES_RE = /^(.+?) intentó compartir su pantalla contigo$/;
// El DM del sistema al INSPECTOR por el mismo vencimiento (sweep.ts `dmDelSistema`) lleva la frase
// larga; con la regex anclada de la campana no matcheaba y el teléfono en español lo leía en inglés.
const SHARE_DM_EN_RE = /^(.+?) tried to share their screen with you and the request expired\. Ask them to share again\.$/;
const SHARE_DM_ES_RE = /^(.+?) intentó compartir su pantalla contigo y la solicitud venció\. Pídele que la comparta de nuevo\.$/;

// RECORD (recordings/service, purge y worker guardan el texto en inglés fijo): el DM "X sent you a
// recording: T (m:ss)", el aviso de vencimiento y el del remux fallido, más la línea `[Watch](/rec/N)`.
// Van LÍNEA por línea porque el DM puede llevar delante la nota del que la mandó.
const REC_DM_EN_RE = /^(.+?) sent you a recording: (.+) \((\d+:\d{2})\)$/;
const REC_DM_ES_RE = /^(.+?) te envió una grabación: (.+) \((\d+:\d{2})\)$/;
const REC_EXP_EN_RE = /^Your recording "(.+)" expires in (\d+) days — open it and press Keep to save it\.?$/;
const REC_EXP_ES_RE = /^Tu grabación "(.+)" vence en (\d+) días — ábrela y pulsa Conservar para guardarla\.?$/;
const REC_FAIL_EN_RE = /^We couldn't process "(.+)"\. It still plays, without a seek bar\.$/;
const REC_FAIL_ES_RE = /^No pudimos procesar "(.+)"\. Se reproduce igual, sin barra de avance\.$/;
const REC_WATCH_EN_RE = /^\[Watch\]\((\/rec\/\d+)\)$/;
const REC_WATCH_ES_RE = /^\[Ver\]\((\/rec\/\d+)\)$/;

function localizeRecLine(line: string, lang: string): string | null {
  const l = line.trim();
  if (lang === 'es') {
    const dm = REC_DM_EN_RE.exec(l);
    if (dm) return `${dm[1]!.trim()} te envió una grabación: ${dm[2]} (${dm[3]})`;
    const exp = REC_EXP_EN_RE.exec(l);
    if (exp) return `Tu grabación "${exp[1]}" vence en ${exp[2]} días — ábrela y pulsa Conservar para guardarla.`;
    const fail = REC_FAIL_EN_RE.exec(l);
    if (fail) return `No pudimos procesar "${fail[1]}". Se reproduce igual, sin barra de avance.`;
    const w = REC_WATCH_EN_RE.exec(l);
    if (w) return `[Ver](${w[1]})`;
    return null;
  }
  const dm = REC_DM_ES_RE.exec(l);
  if (dm) return `${dm[1]!.trim()} sent you a recording: ${dm[2]} (${dm[3]})`;
  const exp = REC_EXP_ES_RE.exec(l);
  if (exp) return `Your recording "${exp[1]}" expires in ${exp[2]} days — open it and press Keep to save it.`;
  const fail = REC_FAIL_ES_RE.exec(l);
  if (fail) return `We couldn't process "${fail[1]}". It still plays, without a seek bar.`;
  const w = REC_WATCH_ES_RE.exec(l);
  if (w) return `[Watch](${w[1]})`;
  return null;
}

export function localizeSystemMessage(body: string | null | undefined, lang: string): string {
  if (!body) return '';
  const b = body.trim();
  const es = ES_RE.exec(b);
  if (es && lang !== 'es') return `⚠️ I haven't followed up on ${es[1]!.trim()} · it's in **${es[2]}** (${es[3]}) · I need help`;
  const en = EN_RE.exec(b);
  if (en && lang === 'es') return `⚠️ No he hecho follow up en ${en[1]!.trim()} · está en **${en[2]}** (${en[3]}) · necesito ayuda`;
  const shareEn = SHARE_EN_RE.exec(b);
  if (shareEn && lang === 'es') return `${shareEn[1]!.trim()} intentó compartir su pantalla contigo`;
  const shareEs = SHARE_ES_RE.exec(b);
  if (shareEs && lang !== 'es') return `${shareEs[1]!.trim()} tried to share their screen with you`;
  const dmEn = SHARE_DM_EN_RE.exec(b);
  if (dmEn && lang === 'es') return `${dmEn[1]!.trim()} intentó compartir su pantalla contigo y la solicitud venció. Pídele que la comparta de nuevo.`;
  const dmEs = SHARE_DM_ES_RE.exec(b);
  if (dmEs && lang !== 'es') return `${dmEs[1]!.trim()} tried to share their screen with you and the request expired. Ask them to share again.`;
  // Record: línea por línea; si ninguna cambia se devuelve el body tal cual.
  let cambio = false;
  const lineas = body.split('\n').map((line) => { const r = localizeRecLine(line, lang); if (r != null) cambio = true; return r ?? line; });
  return cambio ? lineas.join('\n') : body;
}
